/**
 *  @swagger
 *  definitions:
 *    RegattaResultDiplomaParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        Name:
 *          description: Name of the RegattaResultDiploma
 *          type: string
 *        Content:
 *          description: Content of the RegattaResultDiploma
 *          type: string
 *
 *  @swagger
 *  definitions:
 *    RegattaResultDiplomaCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultDiplomaParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - Name
 *
 *  @swagger
 *  definitions:
 *    RegattaResultDiplomaQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaResultDiploma
 *              type: string
 *        - $ref: '#/definitions/RegattaResultDiplomaParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaResultDiplomaUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultDiplomaQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaResultDiploma:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultDiplomaCreateParams'
 *        - $ref: '#/definitions/RegattaResultDiplomaUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaResultDiploma:
 *        allOf:
 *          - $ref: '#/definitions/RegattaResultDiploma'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaResultDiploma
 *                $ref: '#/components/schemas/Regatta'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaId: string;
};

export type $Params = {
  Content: null | string;
  Name: string;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Regatta?: $Model<'Regatta'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Content: {
    allowNull: true,
    type: Sequelize.TEXT,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaResultDiploma'> => {
  const RegattaResultDiploma = stack.define(
    'RegattaResultDiploma',
    fields(Sequelize),
    {
      tableName: 'RegattaResultDiplomas',
    },
  );

  RegattaResultDiploma.associate = (models: $ModelClasses) => {
    models.RegattaResultDiploma.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
  };

  RegattaResultDiploma.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaResultDiploma;
});
